// Cave84 — BulkTransferModal (Inventory bulk actions)
// Moves the bottles behind a multi-row selection into one destination cellar
// (F1.6, bulk). Each row is a vintage+cellar+format group, so the quantity is
// selectable per row — moving 2 of 3 Cathiard 2002 is a normal collector move.

import { useMemo, useState } from 'react';
import type { Bottle } from '@cave84/shared-types';
import { useCellars } from '@/hooks/useCellars';
import { useCellarLocations } from '@/hooks/useLocations';
import { useTransferBottles } from '@/hooks/useBottles';
import type { GroupedInventoryRow } from '@/hooks/useInventory';

const C = {
  overlay: 'rgba(0,0,0,0.7)',
  surface: '#1E1B17',
  surfaceAlt: '#232936',
  border: '#3A3530',
  gold: '#C49255',
  text: '#ECE6D4',
  textDim: '#B8B0A2',
  textMuted: '#908977',
  danger: '#E07070',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: C.surfaceAlt,
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  padding: '8px 12px',
  fontSize: 13,
  color: C.text,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: C.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  fontWeight: 600,
  marginBottom: 6,
};

interface Props {
  rows: GroupedInventoryRow[];
  onClose: () => void;
  /** Fired after a successful commit, with the number of bottles moved. */
  onDone: (movedCount: number) => void;
}

export default function BulkTransferModal({ rows, onClose, onDone }: Props) {
  const [toCellarId, setToCellarId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [reason, setReason] = useState('');
  // Per-row quantity, keyed by row.key. Absent key = move the whole group.
  const [qtyByKey, setQtyByKey] = useState<Record<string, number>>({});

  const { data: cellars = [] } = useCellars();
  const { data: locations = [] } = useCellarLocations(toCellarId || undefined);
  const transfer = useTransferBottles();

  const qtyFor = (row: GroupedInventoryRow) => qtyByKey[row.key] ?? row.qty;

  // Rows already sitting in the destination are shown but not moved — logging a
  // cellar-to-itself transfer would pollute the audit trail.
  const { movable, alreadyThere } = useMemo(() => {
    const movableRows: GroupedInventoryRow[] = [];
    const staying: GroupedInventoryRow[] = [];
    for (const row of rows) {
      if (toCellarId && row.cellar.id === toCellarId) staying.push(row);
      else movableRows.push(row);
    }
    return { movable: movableRows, alreadyThere: staying };
  }, [rows, toCellarId]);

  const bottlesToMove = useMemo<Bottle[]>(
    () => movable.flatMap(row => row.bottles.slice(0, qtyFor(row))),
    // qtyByKey drives qtyFor; listing it keeps the memo honest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [movable, qtyByKey]
  );

  const totalBottles = bottlesToMove.length;
  const canSubmit = !!toCellarId && totalBottles > 0 && !transfer.isPending;

  function setQty(key: string, next: number, max: number) {
    setQtyByKey(prev => ({ ...prev, [key]: Math.min(Math.max(next, 0), max) }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const moved = await transfer.mutateAsync({
      bottles: bottlesToMove,
      toCellarId,
      toLocationId: toLocationId || undefined,
      reason: reason.trim() || undefined,
    });
    onDone(moved);
  }

  const destination = cellars.find(c => c.id === toCellarId);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 300, background: C.overlay,
        display: 'grid', placeItems: 'center', padding: 16,
      }}
    >
      <div
        style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
          width: 'min(560px, 100%)', maxHeight: 'calc(100vh - 64px)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        }}
      >
        <header style={{ padding: '22px 24px 16px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 21, fontWeight: 500, color: C.text, margin: 0 }}>
            Transfer Bottles
          </h2>
          <p style={{ fontSize: 12.5, color: C.textMuted, margin: '6px 0 0' }}>
            {rows.length} {rows.length === 1 ? 'group' : 'groups'} selected
            {totalBottles > 0 && <> &middot; {totalBottles} {totalBottles === 1 ? 'bottle' : 'bottles'} will move</>}
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
        >
          <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1 }}>
            <div style={{ marginBottom: 18 }}>
              <label htmlFor="bulk-transfer-cellar" style={labelStyle}>Destination cellar</label>
              <select
                id="bulk-transfer-cellar"
                value={toCellarId}
                onChange={e => { setToCellarId(e.target.value); setToLocationId(''); }}
                required
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="" disabled>Select cellar…</option>
                {cellars.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {locations.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <label htmlFor="bulk-transfer-location" style={labelStyle}>Location (optional)</label>
                <select
                  id="bulk-transfer-location"
                  value={toLocationId}
                  onChange={e => setToLocationId(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">None</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: 18 }}>
              <label htmlFor="bulk-transfer-reason" style={labelStyle}>Reason (optional)</label>
              <input
                id="bulk-transfer-reason"
                type="text"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Consolidating, travelling, gifting storage…"
                style={inputStyle}
              />
            </div>

            <span style={labelStyle}>Quantity per group</span>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflow: 'hidden' }}>
              {movable.map((row, i) => {
                const qty = qtyFor(row);
                return (
                  <div
                    key={row.key}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                      borderTop: i > 0 ? `1px solid ${C.surfaceAlt}` : 'none',
                      opacity: qty === 0 ? 0.45 : 1,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                      <div style={{ fontSize: 13, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.producer.name}
                      </div>
                      <div style={{ fontSize: 11.5, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.vintage.year === 1001 ? 'NV' : row.vintage.year} &middot; {row.wine.cuvee_name ?? '—'} &middot; {row.cellar.name}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <StepButton label={`Decrease quantity for ${row.producer.name}`} disabled={qty <= 0} onClick={() => setQty(row.key, qty - 1, row.qty)}>&minus;</StepButton>
                      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: C.text, minWidth: 34, textAlign: 'center' }}>
                        {qty}/{row.qty}
                      </span>
                      <StepButton label={`Increase quantity for ${row.producer.name}`} disabled={qty >= row.qty} onClick={() => setQty(row.key, qty + 1, row.qty)}>+</StepButton>
                    </div>
                  </div>
                );
              })}

              {alreadyThere.map((row, i) => (
                <div
                  key={row.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                    borderTop: (movable.length > 0 || i > 0) ? `1px solid ${C.surfaceAlt}` : 'none',
                    opacity: 0.5,
                  }}
                >
                  <div style={{ minWidth: 0, flex: '1 1 auto', fontSize: 13, color: C.textDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.producer.name} &middot; {row.vintage.year === 1001 ? 'NV' : row.vintage.year}
                  </div>
                  <span style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>
                    already here
                  </span>
                </div>
              ))}
            </div>

            {transfer.isError && (
              <p style={{ fontSize: 12, color: C.danger, marginTop: 14 }}>
                {(transfer.error as Error).message}
              </p>
            )}
          </div>

          <footer style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '14px 24px', borderTop: `1px solid ${C.border}` }}>
            <button
              type="button"
              onClick={onClose}
              disabled={transfer.isPending}
              style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 4, padding: '9px 18px', fontSize: 13, color: C.textDim, cursor: transfer.isPending ? 'default' : 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{ background: C.gold, border: 'none', borderRadius: 4, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: '#141821', cursor: canSubmit ? 'pointer' : 'default', opacity: canSubmit ? 1 : 0.5 }}
            >
              {transfer.isPending
                ? 'Transferring…'
                : `Transfer${totalBottles > 0 ? ` ${totalBottles}` : ''}${destination ? ` to ${destination.name}` : ''}`}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function StepButton({ children, onClick, disabled, label }: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      style={{
        width: 26, height: 26, display: 'grid', placeItems: 'center',
        background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 3,
        color: disabled ? '#4A453E' : C.textDim, fontSize: 14, lineHeight: 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}
